import { useState, type KeyboardEvent } from "react";

import type { TaskItem } from "@domain/task-state";

import { TaskCheck } from "./TaskCheck";

export interface HeldTaskCardProps {
  item: TaskItem;
  /**
   * When set, the HEADER ROW navigates to the task's detail page
   * (`role="link"`). Link scope is the header only — the card body can
   * carry the decision form without click-through conflicts.
   */
  onOpen?: () => void;
  /**
   * Relay a decision answer (praxis-intent-relay §3.7). Rendered ONLY
   * on decision cards; `waiting_for` cards stay action-free.
   */
  onResolveDecision?: (answer: string) => void | Promise<void>;
  /** A resolve relay is in flight — form stays mounted, disabled. */
  pending?: boolean;
  /** Inline relay error copy (failed / unconfirmed). */
  error?: string | null;
}

/**
 * A "waiting on you" card. Decision cards with a resolve handler render
 * a free-text answer form ("Record decision"); waiting-for cards render
 * context pills only, with zero action affordances.
 */
export function HeldTaskCard({
  item,
  onOpen,
  onResolveDecision,
  pending,
  error,
}: HeldTaskCardProps) {
  const [answer, setAnswer] = useState("");
  const reasonLabel =
    item.heldReason === "waiting_for" ? "waiting for" : "needs you";

  const showDecisionForm =
    item.heldReason === "decision" && onResolveDecision !== undefined;
  const busy = pending === true;
  const canRecord = showDecisionForm && !busy && answer.trim().length > 0;

  const record = () => {
    if (!canRecord || onResolveDecision === undefined) return;
    void onResolveDecision(answer.trim());
  };

  const headerInteractive =
    onOpen !== undefined
      ? {
          role: "link" as const,
          tabIndex: 0,
          onClick: onOpen,
          onKeyDown: (e: KeyboardEvent<HTMLDivElement>) => {
            if (e.target !== e.currentTarget) return;
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              onOpen();
            }
          },
        }
      : {};

  return (
    <div className="rounded-2xl bg-home-butter/50 dark:bg-white/5 p-4">
      <div
        {...headerInteractive}
        className={`flex items-center gap-2 rounded-lg ${
          onOpen !== undefined
            ? "cursor-pointer transition-colors hover:bg-home-butter/70 dark:hover:bg-white/10 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-home-ink/40 dark:focus-visible:outline-white/40"
            : ""
        }`}
      >
        <TaskCheck state="held" />
        <span className="min-w-0 flex-1 truncate font-body text-[14px] font-semibold text-home-ink dark:text-white">
          {item.title}
        </span>
        <span className="ml-auto shrink-0 font-body text-[12px] text-home-ink/50 dark:text-white/50">
          {reasonLabel}
        </span>
      </div>

      {item.description || item.waiter ? (
        <div className="mt-3 flex flex-wrap gap-2 pl-[30px]">
          {item.waiter ? (
            <span className="rounded-full bg-white dark:bg-surface-dark-card px-3.5 py-1.5 font-body text-[12px] font-semibold text-home-ink dark:text-white shadow-[0_6px_16px_-11px_rgba(33,31,28,0.5)]">
              {item.waiter}
            </span>
          ) : null}
          {item.description ? (
            <span className="rounded-full bg-white dark:bg-surface-dark-card px-3.5 py-1.5 font-body text-[12px] text-home-ink/70 dark:text-white/70 shadow-[0_6px_16px_-11px_rgba(33,31,28,0.5)]">
              {item.description}
            </span>
          ) : null}
        </div>
      ) : null}

      {showDecisionForm ? (
        <div className="mt-3 pl-[30px]">
          <textarea
            value={answer}
            onChange={(e) => setAnswer(e.target.value)}
            disabled={busy}
            rows={2}
            placeholder="Type your decision…"
            aria-label={`Decision for "${item.title}"`}
            className="w-full resize-none rounded-xl border border-home-ink/10 dark:border-white/10 bg-white dark:bg-surface-dark-card px-3 py-2 font-body text-[13px] text-home-ink dark:text-white placeholder:text-home-ink/40 dark:placeholder:text-white/40 outline-none focus:border-home-ink/30 dark:focus:border-white/30 disabled:opacity-60"
          />
          <div className="mt-2 flex items-center gap-3">
            <button
              type="button"
              onClick={record}
              disabled={!canRecord}
              aria-busy={busy}
              className="rounded-full bg-home-ink dark:bg-white px-3.5 py-1.5 font-body text-[12px] font-semibold text-white dark:text-home-ink transition-opacity disabled:opacity-30"
            >
              {busy ? "Recording…" : "Record decision"}
            </button>
            {error != null ? (
              <span className="min-w-0 truncate font-body text-[12px] text-home-terracotta">
                {error}
              </span>
            ) : null}
          </div>
        </div>
      ) : (
        <p className="mt-3 pl-[30px] font-body text-[11px] text-home-ink/45 dark:text-white/45">
          {item.heldReason === "decision"
            ? "Reply in chat to decide"
            : "Your agent is waiting on someone else"}
        </p>
      )}
    </div>
  );
}
